import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, ShieldCheck, AlertTriangle, CheckCircle2, AlertOctagon, MinusCircle, Loader2, Sparkles } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICON_MAP = { pass: CheckCircle2, fail: AlertOctagon, warn: MinusCircle };
const COLOR_MAP = { pass: "#00FF66", fail: "#FF3B30", warn: "#FF9F0A" };

export default function VerificationReportModal({ profileId, status, onClose }) {
  const { t, lang } = useLanguage();
  const locale = lang === "en" ? "en-US" : "it-IT";
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!profileId) return;
    setLoading(true);
    axios
      .get(`${API}/profiles/${profileId}/verification-report`, { withCredentials: true })
      .then(r => setReport(r.data))
      .catch(() => setError(t("verif.report.error")))
      .finally(() => setLoading(false));
  }, [profileId, t]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isFraud = status === "fraud";
  const accent = isFraud ? "#FF3B30" : "#00FF66";
  const HeaderIcon = isFraud ? AlertTriangle : ShieldCheck;

  // Render the right body slot based on async state. Extracted to avoid
  // a deeply nested ternary in JSX.
  function renderBody() {
    if (loading) {
      return (
        <div className="text-center py-16 text-[#A0AEC0]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" style={{ color: accent }} />
          <p className="text-xs tracking-widest uppercase">{t("verif.report.loading")}</p>
        </div>
      );
    }
    if (error) {
      return <div className="text-center py-16 text-[#FF3B30] text-sm">{error}</div>;
    }
    if (!report) return null;
    return (
      <>
        {/* Score panel */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-5 flex items-center gap-6">
          <div className="flex-shrink-0">
            <div className="text-[10px] tracking-widest uppercase text-[#A0AEC0] mb-1">
              {t("verif.report.score")}
            </div>
            <div className="text-5xl font-black leading-none" style={{ color: accent, fontFamily: "Chivo, sans-serif" }}>
              {report.score}
            </div>
            <div className="text-[10px] text-[#A0AEC0] mt-1">/ 100</div>
          </div>
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase border rounded-full"
                  style={{ color: accent, borderColor: `${accent}60`, backgroundColor: `${accent}14` }}>
              <HeaderIcon className="w-3 h-3" style={{ color: accent }} />
              {isFraud ? t("verif.fraud") : t("verif.verified")}
            </span>
            <p className="text-sm text-[#CBD5E1] mt-3 leading-relaxed">{report.summary}</p>
          </div>
        </div>

        {/* Signals list */}
        <div>
          <p className="text-[10px] tracking-widest uppercase text-[#A0AEC0] mb-3">
            {t("verif.report.signals")} · {report.signals.length}
          </p>
          <div className="space-y-2">
            {report.signals.map((s, i) => {
              const Icon = ICON_MAP[s.result] || CheckCircle2;
              const c = COLOR_MAP[s.result] || "#00E5FF";
              return (
                <div key={`${s.category}-${i}`} data-testid={`verif-signal-${s.result}`}
                     className="bg-[#050505] border border-white/[0.04] p-4 flex items-start gap-3 hover:border-white/[0.06] transition-colors">
                  <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: c }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white">{s.category}</span>
                      <span className="text-[9px] px-1.5 py-0.5 border rounded-full tracking-widest uppercase font-bold"
                            style={{ color: c, borderColor: `${c}50`, backgroundColor: `${c}12` }}>
                        {t(`verif.report.outcome.${s.result}`)}
                      </span>
                    </div>
                    <p className="text-xs text-[#A0AEC0] mt-1 leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recommendation */}
        <div className="bg-[#0D0F12] border border-white/[0.04] p-5" style={{ borderTopColor: accent, borderTopWidth: 2 }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: accent }} />
            <p className="text-[10px] tracking-widest uppercase text-[#A0AEC0]">
              {t("verif.report.recommendation")}
            </p>
          </div>
          <p className="text-sm text-white leading-relaxed">{report.recommendation}</p>
        </div>

        <p className="text-[10px] text-[#A0AEC0] text-center tracking-widest uppercase">
          {t("verif.report.generated")}: {new Date(report.generated_at).toLocaleString(locale)}
        </p>
      </>
    );
  }

  return (
    <div data-testid="verif-report-modal" className="fixed inset-0 flex" style={{ zIndex: 9999 }}>
      <div className="flex-1 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="h-full w-full max-w-[720px] bg-[#0A0C10] border-l-2 flex flex-col overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.6)]"
           style={{ borderLeftColor: accent }}>
        {/* Header */}
        <div className="flex items-start gap-3 px-6 py-5 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border rounded-full"
               style={{ borderColor: `${accent}60`, backgroundColor: `${accent}14` }}>
            <HeaderIcon className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-widest uppercase" style={{ color: accent }}>
              {t("verif.report.eyebrow")}
            </p>
            <h3 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
              {report?.profile_name || "—"}
            </h3>
            <p className="text-xs text-[#A0AEC0] mt-0.5">{report?.engine || "Intelligence DB Verification Engine"}</p>
          </div>
          <button data-testid="verif-report-close" onClick={onClose}
                  className="text-[#A0AEC0] hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {renderBody()}
        </div>
      </div>
    </div>
  );
}
